
import {Apollo, gql} from 'apollo-angular';
import {of as observableOf, Observable} from 'rxjs';
import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ProgressService} from './progress.service';
import {AuthenticationService} from '../../authentication/authentication.service';


import {ActivatedRouteSnapshot} from '@angular/router';
import {first, map, mergeMap, share} from "rxjs/operators";

const produceReloadConstituentNationalTeamsGql = () => {
    return gql`
    query ReloadConstituentNationalTeamsGql($individualConstituentId: [Int!]) {
        IndividualConstituent(individualConstituentId: $individualConstituentId) {
            nationalTeams {
                teamId
                name
                description
                imageId
                imagePath
                fundraiser {
                    pastNationalTeam
                    pageId
                    fullUrl
                }
                campaign {
                    campaignId
                    fiscalYear
                    calendarYear
                    programGroup {
                        programGroupId
                        name
                        description
                        baseUrl
                        imageUrl
                    }
                }
            }
        }
    }
    `;
};

const produceReloadNationalTeamRelatedDateGql = () => {
    return gql`
    query ReloadNationalTeamDataGql($individualConstituentId: [Int!], $teamId: [Int!]) {
        IndividualConstituent(individualConstituentId: $individualConstituentId) {
            nationalTeams(teamId: $teamId) {
                teamId
                name
                description
                imageId
                imagePath
                fundraiser {
                    pastNationalTeam
                    pageId
                    fullUrl
                    fundraiserPosts {
                        fundraiserPostId
                        publishedFl
                        urlKey
                        rawHtmlContent
                        htmlContent
                        publishedDate
                        title
                    }
                }
                children {
                    teamId
                    parentTeamId
                    parentTeamName
                    name
                    description
                    captains {
                        captainName
                        firstName
                        lastName
                        email
                        phoneNumber
                        areaCode
                    }
                    walkDate
                    walkLocation
                    numberOfWalkers
                    chapter
                    donationsReceived
                    outstandingSponsorships
                    outstandingMatchingGifts
                    total
                    teamFundraisingGoal
                }
                campaign {
                    campaignId
                    fiscalYear
                    calendarYear
                    programGroup {
                        programGroupId
                        name
                        description
                        baseUrl
                        imageUrl
                    }
                }
            }
        }
    }
    `;
};

@Injectable()
export class NationalTeamsService {

    public nationalTeams = [];
    public pastNationalTeams = [];
    public allNationalTeams = null;
    public selectedNationalTeam: any;
    public selectedNationalTeamKey;

    // we need this for resolver to wait
    public teamRelatedStuffObservable: Observable<any>;
    public onNationalTeamInit: EventEmitter<any> = new EventEmitter();
    public onNationalTeamChange: EventEmitter<any> = new EventEmitter();

    constructor(
        private http: HttpClient,
        private progressService: ProgressService,
        private authenticationService: AuthenticationService,
        private apollo: Apollo
    ) {}



    reloadConstituentNationalTeams(route: ActivatedRouteSnapshot) {

        return this.apollo.query<any>({
            fetchPolicy: 'network-only',
            query: produceReloadConstituentNationalTeamsGql(),
            variables: {
                individualConstituentId: this.authenticationService.user.constituentID
            }
        }).pipe(map(res => {
            const nationalTeams = this.parseNationalTeams(route, res.data.IndividualConstituent[0].nationalTeams);
            this.nationalTeams = this.filterNationalTeams(nationalTeams, false);
            this.pastNationalTeams = this.filterNationalTeams(nationalTeams, true);

            return this.nationalTeams;

        }), mergeMap(nationalTeams => {
            const allNationalTeams = this.getAllNationalTeams();
            if (allNationalTeams.length === 0) {
                return observableOf({});
            }
            // No National Team selected? We need to select a national team to load with. First, go with the obvious default:
            // the first national team in the list.
            let defaultNationalTeam = null;

            if (allNationalTeams.length > 0) {
                defaultNationalTeam = allNationalTeams[0];
            }
            // Load the same National Team from last time we used the app, if there is one.
            const lastNationalTeam = this.getLastNationalTeam(allNationalTeams);
            if (lastNationalTeam != null) {
                this.setNationalTeamInternal(lastNationalTeam, true);
            } else {
                this.setNationalTeamInternal(defaultNationalTeam, true);
            }
            return this.teamRelatedStuffObservable;
        }), first());
    }

    filterNationalTeams(nationalTeams, pastNationalTeamsFlag) {
        return nationalTeams
            .filter(nationalTeam => nationalTeam.isPastNationalTeam === pastNationalTeamsFlag)
            .sort((a, b) => {
                if(a.calendarYear === b.calendarYear) {
                    return a.name > b.name ? -1 : 1;
                } else {
                    return a.calendarYear > b.calendarYear ? -1 : 1;
                }
            });
    }

    sortSubTeams(subTeams) {
        return subTeams
            .sort((a, b) => {
                return a.total > b.total ? -1 : 1;
            });
    }

    parseNationalTeams(route: ActivatedRouteSnapshot, nationalTeams) {
        const programsData = route.parent.data['common']['Program'];
        const processedNationalTeams = [];
        nationalTeams.forEach(nationalTeam => {
            const programData = programsData.find(o => {
                return o.programGroup.name === nationalTeam.campaign.programGroup.name;
            });

            let cssProgramName = '';

            if (programData.programGroup.name === 'S&Y') {
                cssProgramName = 's2';
            } else if (programData.programGroup.name === 'MWOY') {
                cssProgramName = programData.name.toLowerCase();
            } else {
                cssProgramName = programData.programGroup.name.toLowerCase();
            }
            const fundraiser = nationalTeam.fundraiser;
            processedNationalTeams.push({
                teamId: nationalTeam.teamId.toString(),
                name: nationalTeam.name,
                imagePath: nationalTeam.imagePath,
                imageId: nationalTeam.imageId,
                cssClass: 'program-' + cssProgramName,
                programGroupDescription: programData.programGroup.description,
                programGroupName: programData.programGroup.name,
                isPastNationalTeam: !!fundraiser ? fundraiser.pastNationalTeam : false,
                nationalTeamPageId: !!fundraiser ? fundraiser.pageId : null,
                fiscalYear: nationalTeam.campaign.fiscalYear,
                calendarYear: nationalTeam.campaign.calendarYear
            });
        });

        return processedNationalTeams;
    }

    getAllNationalTeams() {
        if (!!this.allNationalTeams && this.allNationalTeams.length > 0) {
            return this.allNationalTeams;
        }
        this.allNationalTeams = [];
        for (let i = 0; i < this.nationalTeams.length; i++) {
            this.allNationalTeams.push(this.nationalTeams[i]);
        }
        for (let i = 0; i < this.pastNationalTeams.length; i++) {
            this.allNationalTeams.push(this.pastNationalTeams[i]);
        }
        return this.allNationalTeams;
    }

    private getLastNationalTeam(allNationalTeams: any) {
        const lastNationalTeam = localStorage.getItem('org.lls.lastNationalTeam');
        if (lastNationalTeam == null) {
            return null;
        }

        if (allNationalTeams != null) {
            for (let i = 0; i < allNationalTeams.length; i++) {
                if (allNationalTeams[i].teamId === lastNationalTeam) {
                    return allNationalTeams[i];
                }
            }
        }
        return null;
    }


    private setNationalTeamInternal(nationalTeam: any, isFirst: boolean = false) {
        this.progressService.startProgress(false);
        this.selectedNationalTeam = nationalTeam;
        this.selectedNationalTeamKey = this.selectedNationalTeam.teamId;
        localStorage.setItem('org.lls.lastNationalTeam', this.selectedNationalTeamKey);

        // load secondary data once event is set
        this.teamRelatedStuffObservable = this.getLoadNationalTeamRelatedDataGql(
            this.selectedNationalTeam.teamId
        ).pipe(share());

        if (isFirst) {
            this.teamRelatedStuffObservable.subscribe(responses => {
                this.onNationalTeamInit.emit(nationalTeam);
            });
        } else {
            this.teamRelatedStuffObservable.subscribe(responses => {
                this.onNationalTeamChange.emit(nationalTeam);
            });
        }
    }

    getLoadNationalTeamRelatedDataGql(teamId) {
        return this.apollo.query<any>({
            fetchPolicy: 'network-only',
            query: produceReloadNationalTeamRelatedDateGql(),
            variables: {
                individualConstituentId: this.authenticationService.user.constituentID,
                teamId: teamId
            }
        }).pipe(map(res => {
            this.selectedNationalTeam.subTeams = this.sortSubTeams(JSON.parse(JSON.stringify(res.data.IndividualConstituent[0].nationalTeams[0].children)));
            this.selectedNationalTeam.fundraiser = JSON.parse(JSON.stringify(res.data.IndividualConstituent[0].nationalTeams[0].fundraiser));
            this.selectedNationalTeam.campaign = JSON.parse(JSON.stringify(res.data.IndividualConstituent[0].nationalTeams[0].campaign));
            return res.data;
        }), first());
    }


    loadCurrentNationalTeamRelatedDataGql() {
        this.getLoadNationalTeamRelatedDataGql(this.selectedNationalTeam.teamId);
    }

    setNationalTeam(nationalTeam: any) {
        this.setNationalTeamInternal(nationalTeam);
        this.teamRelatedStuffObservable.subscribe(x => {
            this.progressService.stopProgress(false);
        });
    }

    numberOfNationalTeams() {
        if (this.nationalTeams) {
            return this.nationalTeams.length + this.pastNationalTeams.length;
        }
    }
}
